import { lazy, Suspense, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider } from "./auth/AuthProvider";
import { I18nProvider, useI18n } from "./i18n";
import { AppShell } from "./layout/AppShell";
import { DashboardPage } from "./routes/DashboardPage";
import { LegacyRedirectPage } from "./routes/LegacyRedirectPage";
import { NotFoundPage } from "./routes/NotFoundPage";

const ActionFormPage = lazy(() =>
  import("./routes/ActionFormPage").then((module) => ({ default: module.ActionFormPage }))
);
const ActionsPage = lazy(() =>
  import("./routes/ActionsPage").then((module) => ({ default: module.ActionsPage }))
);
const AdminPage = lazy(() =>
  import("./routes/AdminPage").then((module) => ({ default: module.AdminPage }))
);
const CommunityRoutesPage = lazy(() =>
  import("./routes/CommunityRoutesPage").then((module) => ({ default: module.CommunityRoutesPage }))
);
const CommunitySegmentsPage = lazy(() =>
  import("./routes/CommunitySegmentsPage").then((module) => ({ default: module.CommunitySegmentsPage }))
);
const EventDetailPage = lazy(() =>
  import("./routes/EventDetailPage").then((module) => ({ default: module.EventDetailPage }))
);
const EventsPage = lazy(() =>
  import("./routes/EventsPage").then((module) => ({ default: module.EventsPage }))
);
const LiveRacePage = lazy(() =>
  import("./routes/LiveRacePage").then((module) => ({ default: module.LiveRacePage }))
);
const ProfilePage = lazy(() =>
  import("./routes/ProfilePage").then((module) => ({ default: module.ProfilePage }))
);
const PublicTrackPage = lazy(() =>
  import("./routes/PublicTrackPage").then((module) => ({ default: module.PublicTrackPage }))
);
const PublicProfilePage = lazy(() =>
  import("./routes/PublicProfilePage").then((module) => ({ default: module.PublicProfilePage }))
);
const ResultsPage = lazy(() =>
  import("./routes/ResultsPage").then((module) => ({ default: module.ResultsPage }))
);
const RouteEditorPage = lazy(() =>
  import("./routes/RouteEditorPage").then((module) => ({ default: module.RouteEditorPage }))
);
const RoutesPage = lazy(() =>
  import("./routes/RoutesPage").then((module) => ({ default: module.RoutesPage }))
);
const SavedRouteDetailPage = lazy(() =>
  import("./routes/SavedRouteDetailPage").then((module) => ({ default: module.SavedRouteDetailPage }))
);
const SegmentLeaderboardPage = lazy(() =>
  import("./routes/SegmentLeaderboardPage").then((module) => ({ default: module.SegmentLeaderboardPage }))
);

function RouteLoadingFallback() {
  const { t } = useI18n();

  return (
    <section className="page">
      <article className="card route-loading-card">
        <strong>{t("app.routeLoadingTitle")}</strong>
        <span>{t("app.routeLoadingBody")}</span>
      </article>
    </section>
  );
}

function lazyRoute(element: ReactNode) {
  return <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>;
}

export default function App() {
  return (
    <I18nProvider>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="u/:uid" element={lazyRoute(<PublicProfilePage />)} />
            <Route element={<AppShell />}>
              <Route index element={<DashboardPage />} />
              <Route path="dashboard" element={<Navigate to="/" replace />} />
              <Route path="events" element={lazyRoute(<EventsPage />)} />
              <Route path="events/new" element={lazyRoute(<ActionFormPage />)} />
              <Route path="events/:eventId" element={lazyRoute(<EventDetailPage />)} />
              <Route path="my-events" element={lazyRoute(<EventsPage managed />)} />
              <Route path="event/:eventId" element={lazyRoute(<EventDetailPage legacyPath />)} />
              <Route path="event/:eventId/edit" element={lazyRoute(<ActionFormPage />)} />
              <Route path="event.html" element={<LegacyRedirectPage type="event" />} />
              <Route path="race/:eventId" element={lazyRoute(<LiveRacePage />)} />
              <Route path="race.html" element={<LegacyRedirectPage type="race" />} />
              <Route path="track/:sessionId" element={lazyRoute(<PublicTrackPage />)} />
              <Route path="track.html" element={<LegacyRedirectPage type="track" />} />
              <Route path="routes" element={lazyRoute(<RoutesPage />)} />
              <Route path="routes/:rideId" element={lazyRoute(<SavedRouteDetailPage />)} />
              <Route path="route-editor" element={lazyRoute(<RouteEditorPage />)} />
              <Route path="route-editor/ride/:rideId" element={lazyRoute(<RouteEditorPage />)} />
              <Route path="route-editor/:eventId" element={lazyRoute(<RouteEditorPage />)} />
              <Route path="community/routes" element={lazyRoute(<CommunityRoutesPage />)} />
              <Route path="community/segments" element={lazyRoute(<CommunitySegmentsPage />)} />
              <Route path="segments/:segmentId" element={lazyRoute(<SegmentLeaderboardPage />)} />
              <Route path="segment-leaderboards" element={lazyRoute(<SegmentLeaderboardPage />)} />
              <Route path="actions" element={lazyRoute(<ActionsPage />)} />
              <Route path="actions/new" element={lazyRoute(<ActionFormPage />)} />
              <Route path="actions/:eventId/edit" element={lazyRoute(<ActionFormPage />)} />
              <Route path="admin" element={lazyRoute(<AdminPage />)} />
              <Route path="admin/users/:uid" element={lazyRoute(<AdminPage />)} />
              <Route path="results" element={lazyRoute(<ResultsPage />)} />
              <Route path="profile" element={lazyRoute(<ProfilePage />)} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </I18nProvider>
  );
}
